import { useTranslation } from 'react-i18next';
import { CURRENCY_DECIMALS, CurrencyEnum } from '@restavo/shared/enums/currency';
import { formatMoney } from '@restavo/shared/money';
import { useRestaurantCurrency } from 'src/hooks/useRestaurants';

/** The UI language drives the number style; the restaurant drives the currency. */
const LOCALE_TAGS: Record<string, string> = { vi: 'vi-VN', en: 'en-US' };

export function useMoney() {
  const { i18n } = useTranslation();
  // Member-readable (unlike useCurrentRestaurant, owner/admin only) — POS/Kitchen/Delivery/`me`
  // callers need this to work too. Falls back to VND while loading / for roles below member.
  const { data } = useRestaurantCurrency();
  const currency = (data?.currency as CurrencyEnum | undefined) ?? CurrencyEnum.VND;
  const locale = LOCALE_TAGS[i18n.language] ?? 'en-US';
  return {
    currency,
    decimals: CURRENCY_DECIMALS[currency],
    usesThousandsShortcut: currency === CurrencyEnum.VND,
    // True once the currency has been fetched — display can fall back to VND, but an input must
    // not accept amounts typed against a currency that isn't confirmed yet. `data`, not
    // `isSuccess`: a failed background refetch flips status to 'error' while the cached
    // currency is still right, and that must not disable every money field.
    isKnown: data !== undefined,
    format: (amount: number | null | undefined) => formatMoney(amount, currency, locale),
    formatCompact: (amount: number | null | undefined) => formatMoney(amount, currency, locale, { compact: true }),
  };
}
